// blog/pages/blog.js
import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import ErrorPage from 'next/error';
import Link from 'next/link';
import Head from "next/head";
import styles from './BlogPost.module.css';
import Navbar from '../../preusable/Navbar';
import { Minimize2, Maximize2 } from 'lucide-react';
import defaultNavbarContent from "../../preconfigs/navbar";
import { slugify } from '../../utils/slugify';
import ErrorBoundary from './ErrorBoundary';

const linkStyle = {
  textDecoration: 'none',
  color: 'inherit'
};

const EditMenu = ({ blogPostContent, updateBlogPostContent, onClose }) => {
  return (
    <div style={{width: '550px'}} className="fixed z-50 right-0 top-0 h-full w-140 bg-gray-100 shadow-2xl p-6 overflow-y-auto transition-transform duration-300 ease-in-out transform translate-x-0">
      <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Edit Blog Post Page</h2>
      <div className="space-y-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="font-semibold mb-3 text-lg text-gray-700">Layout</h3>
          <label className="flex items-center space-x-3 mb-3">
            <input 
              type="checkbox" 
              checked={blogPostContent.showTableOfContents} 
              onChange={(e) => updateBlogPostContent('showTableOfContents', e.target.checked)}
              className="w-5 h-5 text-gray-500 border-gray-300 rounded focus:ring-gray-500"
            />
            <span className="text-gray-700">Show Table of Contents</span>
          </label>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="font-semibold mb-3 text-lg text-gray-700">Colors</h3>
          <div className="flex items-center space-x-2 mb-3">
            <label className="text-sm font-medium text-gray-700">Page Background:</label>
            <input 
              type="color" 
              value={blogPostContent.pageBackgroundColor} 
              onChange={(e) => updateBlogPostContent('pageBackgroundColor', e.target.value)}
              className="h-10 w-10"
            />
          </div>
          <div className="flex items-center space-x-2 mb-3">
            <label className="text-sm font-medium text-gray-700">Table of Contents Text:</label>
            <input 
              type="color" 
              value={blogPostContent.tocTextColor} 
              onChange={(e) => updateBlogPostContent('tocTextColor', e.target.value)}
              className="h-10 w-10"
            />
          </div>
          <div className="flex items-center space-x-2 mb-3">
            <label className="text-sm font-medium text-gray-700">Title Text:</label>
            <input 
              type="color" 
              value={blogPostContent.titleTextColor} 
              onChange={(e) => updateBlogPostContent('titleTextColor', e.target.value)}
              className="h-10 w-10"
            />
          </div>
          <div className="flex items-center space-x-2 mb-3">
            <label className="text-sm font-medium text-gray-700">Article Text:</label>
            <input 
              type="color" 
              value={blogPostContent.articleTextColor} 
              onChange={(e) => updateBlogPostContent('articleTextColor', e.target.value)}
              className="h-10 w-10"
            />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="font-semibold mb-3 text-lg text-gray-700">Text Size</h3>
          <div className="flex items-center space-x-2 mb-3">
            <label className="text-sm font-medium text-gray-700">Title Text Size:</label>
            <input 
              type="number" 
              value={blogPostContent.titleTextSize} 
              onChange={(e) => updateBlogPostContent('titleTextSize', e.target.value)}
              className="w-20 p-2 border rounded focus:ring-2 focus:ring-gray-300 focus:border-transparent"
              min="16"
              max="72"
            />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="font-semibold mb-3 text-lg text-gray-700">Author Information</h3>
          <label className="flex items-center space-x-3 mb-3">
            <input 
              type="checkbox" 
              checked={blogPostContent.showAuthorInfo} 
              onChange={(e) => updateBlogPostContent('showAuthorInfo', e.target.checked)}
              className="w-5 h-5 text-gray-500 border-gray-300 rounded focus:ring-gray-500"
            />
            <span className="text-gray-700">Show Author Information</span>
          </label>
        </div>
      </div>
    </div>
  );
};

const TableOfContents = ({ toc, activeId, tocTextColor }) => {
  return (
    <div className={styles.tableOfContents}>
      <nav>
        <ul>
          {toc.map((item) => (
            <li 
              key={item.id} 
              className={`${styles[`tocLevel${item.level}`]} ${item.id === activeId ? styles.active : ''}`}
            >
              <a href={`#${item.id}`} style={{color: tocTextColor}}>{item.text}</a>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};

const AuthorInfo = ({ author, showAuthorInfo }) => {
  if (!showAuthorInfo) return null;

  return (
    <div className="mt-8 p-6 rounded-lg shadow-md">
      <div className="flex items-center">
        {author.image && (
          <img src={author.image} alt={author?.name} className="w-16 h-16 rounded-full mr-4" />
        )}
        <div>
          <h3 className="text-xl font-bold">{author?.name}</h3>
          <p className="text-gray-600 mt-2">{author?.bio}</p>
        </div>
      </div>
    </div>
  );
};

const defaultBlogPostContent = {
  showTableOfContents: true,
  pageBackgroundColor: '#ffffff',
  tocTextColor: '#333333',
  titleTextColor: '#000000',
  titleTextSize: '40',
  articleTextColor: '#333333',
  showAuthorInfo: true,
};

const Tags = ({ tags }) => {
  if (!tags || tags.length === 0) return null;

  const router = useRouter();

  const handleTagClick = () => {
    window.parent.postMessage({ type: 'LOADING' }, '*');
  };

  return (
    <div className="mb-5">
      <div className="flex flex-wrap gap-2">
        {tags.map((tag, index) => (
          <a
            href={`/tags/${slugify(tag)}`}
            key={index}
            style={linkStyle}
            className="bg-gray-200 text-gray-700 px-2 py-1 rounded-md text-sm cursor-pointer hover:bg-gray-300 transition-colors duration-200"
            onClick={() => handleTagClick(tag)}
          >
            {tag}
          </a>
        ))}
      </div>
    </div>
  );
};

const RelatedPosts = ({ relatedPosts, blogLayout }) => {
  const renderDate = (date) => {
    if (!date || !blogLayout.showDates) {
      return null;
    }

    const jsDate = new Date(date._seconds * 1000 + date._nanoseconds / 1000000);

    return (
      <p className="text-sm text-gray-500 mt-1">
        {format(jsDate, 'MMMM d, yyyy')}
      </p>
    );
  };

  const truncateTitle = (title, wordLimit = 9) => {
    const words = title.trim().split(/\s+/);
    if (words.length <= wordLimit) {
      return title;
    }
    return words.slice(0, wordLimit).join(' ') + '...';
  };

  const handleNavigation = () => {
    window.parent.postMessage({ type: 'LOADING' }, '*');
  }

  if (!relatedPosts || relatedPosts.length === 0) {
    return null;
  }

  return (
    <div className="mt-12">
      <p className='mb-7'>Related Posts</p>
      <ul className="space-y-6 pl-0">
        {relatedPosts.map(post => (
          <li key={post?.slug} className="bg-white rounded-lg shadow-md hover:shadow-xl overflow-hidden">
            <a href={`/blog/${post?.slug}`} style={linkStyle}>
              <div onClick={handleNavigation} className="flex items-center p-4 space-x-4 cursor-pointer">
                <img
                  src={post?.featuredImageUrl} 
                  alt="Featured" 
                  className="w-20 h-20 object-cover rounded-md"
                />
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2" style={{ color: blogLayout.titleColor }}>
                    {truncateTitle(post?.title)}
                  </h3>
                  {renderDate(post?.date)}
                  <p className='my-0 text-sm' style={{ color: blogLayout.excerptColor }}>
                    {post?.excerpt.slice(0, 120) + "..." || "Click to read more..."}
                  </p>
                </div>
              </div>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};

const RegenerationBox = ({ slug, apiKey }) => {
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [progress, setProgress] = useState(100);
  const [message, setMessage] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const regenerateArticle = async () => {
    setIsRegenerating(true);
    setMessage('');
    setProgress(100);

    try {
      const response = await fetch('https://us-central1-seopublishpro-e6f5b.cloudfunctions.net/callable-regenerate_article', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            apiKey,
            slug
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to regenerate article');
      }

      const { result } = await response.json();

      console.log('response.data.message: ', result);

      setTimeout(() => {
        window.location.replace("/blog/" + result.slug);
      }, 1000);

      setMessage('Article will be regenerated shortly.');
    } catch (error) {
      console.error('Error regenerating article:', error);
      setMessage('Failed to regenerate article. Please try again.');
      setIsRegenerating(false);
    }
  };

  useEffect(() => {
    let timer;
    if (isRegenerating && progress > 0) {
      timer = setInterval(() => {
        if (progress <= 0) {
          window.location.reload();
        }

        setProgress((prevProgress) => {
          const newProgress = prevProgress - (100 / 45);
          return newProgress > 0 ? newProgress : 0;
        });
      }, 1000);
    } else if (progress === 0 && isRegenerating) {
      setIsRegenerating(false);
    }
    return () => clearInterval(timer);
  }, [progress, isRegenerating]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.ceil(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  return (
    <div
      className={`fixed right-4 top-1/2 transform -translate-y-1/2 bg-white rounded-lg shadow-md z-50 transition-all duration-300 ease-in-out ${
        isMinimized ? 'w-12 h-12' : 'w-56 p-4'
      } ${
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      }`}
    >
      <button
        onClick={toggleMinimize}
        className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
      >
        {isMinimized ? <Maximize2 size={20} /> : <Minimize2 size={20} />}
      </button>
      
      {!isMinimized && (
        <>
          {!isRegenerating && progress === 100 ? (
            <>
              <h3 className="text-sm font-semibold mb-2">Blog post has issues?</h3>
              <p className="text-xs mb-5">If you spot issues with an image or are unhappy with any part of the blog post, you can simply regenerate it.</p>
              <button
                onClick={regenerateArticle}
                className="w-full bg-blue-500 text-white px-2 py-1 text-xs rounded hover:bg-blue-600 transition duration-300"
              >
                Regenerate Blog Post
              </button>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center mt-10">
              <div className="w-full bg-blue-600 rounded-full h-2.5 mb-4">
                <div 
                  className="bg-gray-200 h-2.5 rounded-full transition-all duration-1000 ease-linear" 
                  style={{ width: `${100 - progress}%` }}
                ></div>
              </div>
              <p className="text-sm font-medium mb-2">Regenerating post?...</p>
              {progress > 0 && (
                <p className="text-xs text-gray-500">
                  Estimated time: {formatTime(progress * 0.45)}
                </p>
              )}
            </div>
          )}
          {message && <p className="text-sm mt-3 text-center">{message}</p>}
        </>
      )}
    </div>
  );
};
  
  const BlogPost = ({ initialPost, slug, error, initialBlogPostContent, initialNavbarContent }) => {
    const router = useRouter();
    const [apiKey, setApiKey] = useState("");
    const [post, setPost] = useState(initialPost);
    const [blogPostContent, setBlogPostContent] = useState({
      ...defaultBlogPostContent,
      ...initialBlogPostContent
    });
    const [navbarContent, setNavbarContent] = useState({
      ...defaultNavbarContent,
      ...initialNavbarContent
    });
    const [loading, setLoading] = useState(!initialPost);
    const [editMode, setEditMode] = useState(false);
    const [synced, setSynced] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [activeId, setActiveId] = useState('');
    const [showEditMenu, setShowEditMenu] = useState(false);
    const [isArticleHovered, setIsArticleHovered] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showRegenerationBox, setShowRegenerationBox] = useState(false);
  
    useEffect(() => {
      if (typeof window !== 'undefined') {
        window.parent.postMessage({ type: 'ROUTED', path: "/blog" }, '*');
        window.parent.postMessage({ type: 'FINISHED_LOADING' }, '*');
        
        const savedBlogPostContent = localStorage.getItem('blogPostContent');
        if (savedBlogPostContent) {
          setBlogPostContent(JSON.parse(savedBlogPostContent));
        }
  
        const savedNavbarContent = localStorage.getItem('navbarContent');
        if (savedNavbarContent) {
          setNavbarContent(JSON.parse(savedNavbarContent));
        }
      }
    }, []);
  
    const updateNavbarContent = (key, value) => {
      setNavbarContent(prev => {
        const updated = { ...prev, [key]: value };
        localStorage.setItem('navbarContent', JSON.stringify(updated));
        setHasUnsavedChanges(true);
        return updated;
      });
    };
  
    const updateBlogPostContent = (key, value) => {
      setBlogPostContent(prev => {
        const updated = { ...prev, [key]: value };
        localStorage.setItem('blogPostContent', JSON.stringify(updated));
        setHasUnsavedChanges(true);
        return updated;
      });
    };
  
    useEffect(() => {
      const fetchPost = async () => {
        if (!post && apiKey) {
          try {
            if (slug) {
              await fetch('https://us-central1-seopublishpro-e6f5b.cloudfunctions.net/callable-post_visit', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ data: {
                  slug
                } }),
              });
            }
  
            const configData = await fetch('https://us-central1-seopublishpro-e6f5b.cloudfunctions.net/callable-get_blog_config', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                data: {
                  apiKey
                }
              })
            });
  
            const res = await fetch('https://instablogger.com/api/callable-get_articles', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                data: {
                  apiKey,
                  articleSlugs: [
                    {
                      slug: router.query.slug,
                      featuredImageUrl: ""
                    }
                  ]
                }
              })
            });
  
            if (!res.ok) {
              console.log('Failed to fetch article');
            }
  
            const { result: contentConfig} = await configData.json();
            console.log("config: ", contentConfig);
  
            setBlogPostContent(contentConfig.blogPostContent);
            setNavbarContent(contentConfig.navbarContent);
  
            const data = await res.json();
            const fetchedPost = data?.result?.[0] || null;

            // window.parent.postMessage({ type: 'FINISHED_LOADING' }, '*');
  
            if (fetchedPost) {
              setPost({
                ...fetchedPost,
                toc: fetchedPost?.toc || []
              });
            } else {
              setPost(defaultPost)
            }
          } catch (error) {
            console.error('Error fetching article:', error);
          } finally {
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      };
  
      if (router.query.slug) {
        fetchPost();
      }
    }, [router.query.slug, post, apiKey]);
  
    useEffect(() => {
      const handleScroll = () => {
        const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
        const progress = (window.pageYOffset / totalHeight) * 100;
        setScrollProgress(progress);
  
        // Check which section is currently in view
        const headings = Array.from(document.querySelectorAll('h1, h2, h3, h4, h5, h6'));
        for (let i = headings.length - 1; i >= 0; i--) {
          const heading = headings[i];
          if (heading.getBoundingClientRect().top <= 50) {
            setActiveId(heading.id);
            break;
          }
        }
  
        // Show RegenerationBox after scrolling 100 pixels
        if (window.scrollY > 500) {
          setShowRegenerationBox(true);
        }
      };
  
      window.addEventListener('scroll', handleScroll);
  
      // Show RegenerationBox after 10 seconds
      const timer = setTimeout(() => {
        setShowRegenerationBox(true);
      }, 7000);
  
      return () => {
        window.removeEventListener('scroll', handleScroll);
        clearTimeout(timer);
      };
    }, []);
  
    const saveChanges = async () => {
      setShowEditMenu(false);
      setIsArticleHovered(false);
  
      console.log('apiKey: ', apiKey);
  
      const configItems = {
          headerContent: JSON.parse(localStorage.getItem('headerContent')) || null,
          blogLayout: JSON.parse(localStorage.getItem('blogLayout')) || null,
          footerContent: JSON.parse(localStorage.getItem('footerContent')) || null,
          navbarContent: JSON.parse(localStorage.getItem('navbarContent')) || null,
          blogPostContent: JSON.parse(localStorage.getItem('blogPostContent')) || null
      };
  
      try {
          const response = await fetch('https://us-central1-seopublishpro-e6f5b.cloudfunctions.net/callable-update_blog_configurations', {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                  data: {
                      apiKey,
                      ...configItems
                  }
              }),
          });
  
          if (!response.ok) {
              throw new Error('Failed to save configuration');
          }
  
          const result = await response.json();
          console.log('Configuration saved successfully:', result);
          console.log('Configuration saved successfully');
          
          window.parent.postMessage({ type: 'SAVE_SUCCESSFUL' }, '*');
      } catch (error) {
          console.error('Error saving configuration:', error);
          window.parent.postMessage({ type: 'SAVE_FAILED', error: error.message }, '*');
      }
    };
  
    useEffect(() => {
      let interval;
  
      const emitPageLoaded = () => {
        window.parent.postMessage({ type: 'PAGE_LOADED' }, '*');
      };
  
      const handleMessage = async (event) => {
        const message = event.data;

        if (message.type === 'NAVIGATE') {
          console.log('this code ran abadad');
        //   router.push("/");
        }
  
        if (message.type === 'PAGE_SYNCED') {
          console.log('Received PAGE_SYNCED message:', message);
          setSynced(true);
          setEditMode(message.editMode);
          setApiKey(message.apiKey);
        }
        
        if (message.type === 'EDIT_MODE_STATE') {
          console.log('Received EDIT_MODE_STATE message:', message);
          setEditMode(message.editMode);
        }
  
        if (message.type === 'SAVE_CHANGES') {
          console.log('Received SAVE_CHANGES message:', message);
          saveChanges();
          setHasUnsavedChanges(false);
        }
      };
  
      window.addEventListener('message', handleMessage);
  
      interval = setInterval(() => {
        if (!synced) {
          emitPageLoaded();
        } else {
          clearInterval(interval);
        }
      }, 1000);
  
      return () => {
        window.removeEventListener('message', handleMessage);
        clearInterval(interval);
      };
    }, [synced]);
  
    useEffect(() => {
      if (hasUnsavedChanges) {
        window.parent.postMessage({ type: 'UNSAVED_CHANGES', hasUnsavedChanges: true }, '*');
      }
    }, [hasUnsavedChanges]);
  
    if (error) {
      return <div>Error: {error}</div>;
    }
  
    if (loading || !post) {
      return (
        <div className="flex justify-center items-center h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-gray-900"></div>
        </div>
      );
    }
  
    return (
      <ErrorBoundary>
        <div style={{backgroundColor: blogPostContent.pageBackgroundColor}}>
          <Head>
            <title>{post?.title}</title>
            <meta name="description" content={post?.excerpt || post?.description || ""} />
            <meta property="og:title" content={post?.title || ""} />
            <meta property="og:description" content={post?.excerpt || post?.description || ""} />
            <meta property="og:image" content={post?.featuredImageUrl || ""} />
            <meta property="og:type" content="article" />
            {/* <meta property="og:url" content={window && window.location && window.location.hostname + `/blog/${slug}`} /> */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={post?.title || ""} />
            <meta name="twitter:description" content={post?.excerpt || post?.description || ""} />
            <meta name="twitter:image" content={post?.featuredImageUrl || ""} />
            {/* <link rel="canonical" href={window && window.location && window.location.hostname + `/blog/${slug}`} /> */}
          </Head>
          {showRegenerationBox && synced && <RegenerationBox slug={slug} apiKey={apiKey} />}
    
          <div className="max-w-[1200px] mx-auto mb-4">
            <Navbar 
              editMode={editMode}
              navbarContent={navbarContent} 
              setNavbarContent={setNavbarContent}
              updateNavbarContent={updateNavbarContent}
            />
          </div>
          <div 
            style={{
              marginTop: navbarContent?.isSticky ? '80px': '0px'
            }} 
            className={`${styles.container} relative`}
            onMouseEnter={() => editMode && !showEditMenu && setIsArticleHovered(true)}
            onMouseLeave={() => editMode && !showEditMenu && setIsArticleHovered(false)}
          >
            <div className={styles.content}>
              {blogPostContent.showTableOfContents && (
                <div className='hidden lg:block'>
                    <TableOfContents toc={post?.toc} activeId={activeId} tocTextColor={blogPostContent.tocTextColor} />
                </div>
              )}
              <article className={`${styles.article} relative max-w-3xl mx-auto mb-40`}>
                <h1 
                  className={`mb-5 mt-5 font-bold ${editMode && !showEditMenu && isArticleHovered ? 'blur' : ''} `}
                  style={{
                    fontSize: '35px',
                    color: blogPostContent.titleTextColor,
                  }}
                >
                  {post?.title}
                </h1>
                {editMode && !showEditMenu && isArticleHovered && (
                  <div className="absolute top-60 w-full z-20 mb-4">
                    <div className='w-full'>
                        <button
                        onClick={() => setShowEditMenu(true)}
                        className="bg-black mx-auto text-white px-2 py-1 hover:bg-opacity-75 transition duration-300 ease-in-out flex items-center space-x-2 text-sm"
                        >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                        <span>Edit Blog Post Page</span>
                        </button>
                    </div>
                  </div>
                )}
                <div 
                  className={`${styles.seoautobot} ${editMode && !showEditMenu && isArticleHovered ? 'blur' : ''} transition-all duration-300`}
                  dangerouslySetInnerHTML={{ __html: post?.content }} 
                  style={{color: blogPostContent.articleTextColor}}
                />
                <Tags tags={post?.tags} />
                {/* <AuthorInfo author={post?.author} showAuthorInfo={blogPostContent.showAuthorInfo} /> */}
                <RelatedPosts relatedPosts={post?.similarPosts} blogLayout={blogPostContent} />
              </article>
            </div>
          </div>
          {showEditMenu && (
            <EditMenu
              blogPostContent={blogPostContent}
              updateBlogPostContent={updateBlogPostContent}
              onClose={() => {
                setShowEditMenu(false);
                setIsArticleHovered(false);
              }}
            />
          )}
        </div>
      </ErrorBoundary>
    );
  };
  
  export async function getStaticPaths() {
    if (!process.env.NEXT_PUBLIC_API_KEY) {
      return { paths: [], fallback: 'blocking' };
    }
  
    try {
      const res = await fetch('https://instablogger.com/api/callable-get_slugs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            apiKey: process.env.NEXT_PUBLIC_API_KEY
          }
        })
      });
  
      const data = await res.json();
      const posts = data.result || [];
  
      const paths = posts.map((post) => ({
        params: { slug: post?.slug },
      }));
      return { paths, fallback: false };
    } catch (e) {
      console.log('e: ', e);
      return { paths: [], fallback: false };
    }
  }
  
  export async function getStaticProps({ params }) {
    if (!process.env.NEXT_PUBLIC_API_KEY) {
      return { 
        props: { 
          initialPost: null, 
          slug: params?.slug || "", 
          initialBlogPostContent: defaultBlogPostContent,
          initialNavbarContent: defaultNavbarContent
        } 
      };
    }
  
    try {
      const [articleRes, configRes] = await Promise.all([
        fetch('https://instablogger.com/api/callable-get_articles', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            data: {
              apiKey: process.env.NEXT_PUBLIC_API_KEY,
              articleSlugs: [
                {
                  slug: params.slug,
                  featuredImageUrl: ""
                }
              ]
            }
          })
        }),
        fetch('https://us-central1-seopublishpro-e6f5b.cloudfunctions.net/callable-get_blog_config', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            data: {
              apiKey: process.env.NEXT_PUBLIC_API_KEY
            }
          })
        })
      ]);
  
      if (!articleRes.ok || !configRes.ok) {
        throw new Error('Failed to fetch data');
      }
  
      const [articleData, configData] = await Promise.all([articleRes.json(), configRes.json()]);
  
      const post = articleData?.result?.[0] || null;
      const config = configData?.result || {};
  
      if (!post) {
        return { 
          props: { 
            initialPost: null, 
            slug: params?.slug || "", 
            error: "Article not found",
            initialBlogPostContent: config.blogPostContent || defaultBlogPostContent,
            initialNavbarContent: config.navbarContent || defaultNavbarContent
          } 
        };
      }
  
      return { 
        props: { 
          initialPost: {
            ...post,
            toc: post?.toc || []
          },
          slug: params?.slug || "",
          initialBlogPostContent: config.blogPostContent || defaultBlogPostContent,
          initialNavbarContent: config.navbarContent || defaultNavbarContent
        } 
      };
    } catch (error) {
      console.error('Error fetching data:', error);
      return { 
        props: { 
          initialPost: null, 
          slug: params?.slug || "", 
          error: error.message,
          initialBlogPostContent: defaultBlogPostContent,
          initialNavbarContent: defaultNavbarContent
        } 
      };
    }
  }
  
  export default BlogPost;